import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';

function AdminEditUserForm() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { token, user: adminUser } = useAuth();
    const [userData, setUserData] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [isUploading, setIsUploading] = useState(false);

    // --- NEW STATE for photo-related messages ---
    const [photoMessage, setPhotoMessage] = useState('');

    const fileInputRef = useRef(null);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await fetch(`https://healthcard.searchisend.com/health_api/get_user_details_admin.php?id=${userId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                if (!response.ok) throw new Error(data.message);
                data.isHighlighted = !!parseInt(data.isHighlighted);
                setUserData(data);
            } catch (error) {
                setMessage(`Error: ${error.message}`);
            } finally {
                setIsLoading(false);
            }
        };
        fetchUserData();
    }, [userId, token]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setUserData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handlePhotoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setIsUploading(true);
        setPhotoMessage('Uploading photo...'); // Use photoMessage
        setMessage(''); // Clear main message

        const formData = new FormData();
        formData.append('profilePhoto', file);
        formData.append('userId', userId);
        try {
            const response = await fetch(`https://healthcard.searchisend.com/health_api/update_user_photo_admin.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message);
            setUserData(prev => ({ ...prev, profilePictureUrl: result.profilePictureUrl }));
            setPhotoMessage('Photo uploaded successfully!'); // Use photoMessage
        } catch (error) {
            setPhotoMessage(`Upload Error: ${error.message}`); // Use photoMessage
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    // --- NEW FUNCTION TO HANDLE PHOTO REMOVAL ---
    const handlePhotoRemove = async () => {
        if (!window.confirm('Are you sure you want to permanently remove this user\'s profile photo?')) {
            return;
        }

        setPhotoMessage('Removing photo...');
        setMessage(''); // Clear main message

        try {
            const response = await fetch(`https://healthcard.searchisend.com/health_api/remove_user_photo_admin.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ userId: userId })
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message);

            setUserData(prev => ({ ...prev, profilePictureUrl: null }));
            setPhotoMessage('Photo removed successfully!');
        } catch (error) {
            setPhotoMessage(`Error: ${error.message}`);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('Saving changes...');
        setPhotoMessage(''); // Clear photo message on main save

        const payload = { ...userData };
        if (newPassword) payload.newPassword = newPassword;
        payload.isHighlighted = payload.isHighlighted ? 1 : 0;
        try {
            const response = await fetch(`https://healthcard.searchisend.com/health_api/update_user_admin.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(payload)
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message);
            setMessage('User updated successfully! Redirecting...');
            setTimeout(() => navigate('/portal-management-7a8b9c/manage-members'), 2000);
        } catch (error) {
            setMessage(`Save Error: ${error.message}`);
        }
    };

    if (isLoading) return <p>Loading user data...</p>;
    if (!userData) return <p className="text-red-500">{message || 'User not found.'}</p>;

    return (
        <div className="w-full">
            <h2 className="text-2xl font-semibold text-gray-700 mb-4">Edit Member: {userData.firstName} {userData.lastName}</h2>
            <form onSubmit={handleSubmit} className="space-y-6 bg-gray-50 p-6 rounded-lg">
                <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                        <img
                            src={userData.profilePictureUrl ? `https://healthcard.searchisend.com${userData.profilePictureUrl}` : `https://ui-avatars.com/api/?name=${userData.firstName}+${userData.lastName}&background=8b0000&color=fff&size=128`}
                            alt="Profile Preview"
                            className="w-32 h-32 rounded-lg object-cover border"
                        />
                    </div>
                    <div className="flex-grow space-y-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Change Profile Photo (JPG/PNG, max 2MB)</label>
                            <input
                                type="file"
                                accept="image/jpeg, image/png"
                                onChange={handlePhotoUpload}
                                ref={fileInputRef}
                                disabled={isUploading}
                                className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
                            />
                        </div>
                        {/* --- NEW REMOVE BUTTON --- */}
                        {userData.profilePictureUrl && (
                            <div>
                                <button
                                    type="button"
                                    onClick={handlePhotoRemove}
                                    disabled={isUploading}
                                    className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                                >
                                    Remove Current Photo
                                </button>
                            </div>
                        )}
                        {photoMessage && <p className="text-sm text-gray-600 mt-2">{photoMessage}</p>}
                    </div>
                </div>

                {/* --- Rest of the form (no changes) --- */}

                <h3 className="text-xl font-bold text-gray-700 mt-6 border-b pb-2">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input name="firstName" value={userData.firstName || ''} onChange={handleChange} placeholder="First Name" className="p-2 border rounded" />
                    <input name="lastName" value={userData.lastName || ''} onChange={handleChange} placeholder="Last Name" className="p-2 border rounded" />
                    <input name="email" type="email" value={userData.email || ''} onChange={handleChange} placeholder="Email" className="p-2 border rounded" />
                    <input name="phoneNumber" value={userData.phoneNumber || ''} onChange={handleChange} placeholder="Phone Number" className="p-2 border rounded" />
                    <input name="state" value={userData.state || ''} onChange={handleChange} placeholder="State" className="p-2 border rounded" />
                    <input name="city" value={userData.city || ''} onChange={handleChange} placeholder="City" className="p-2 border rounded" />
                    <div className="relative">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Date of Birth</label>
                        <input type="date" name="dateOfBirth" value={userData.dateOfBirth || ''} onChange={handleChange} className="w-full p-2 border rounded" />
                    </div>
                    <input name="profession" value={userData.profession || ''} onChange={handleChange} placeholder="Profession / Job Title" className="p-2 border rounded self-end" />
                    <div className="relative">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Joining Date</label>
                        <input type="date" name="joiningDate" value={userData.joiningDate ? new Date(userData.joiningDate).toISOString().split('T')[0] : ''} onChange={handleChange} className="w-full p-2 border rounded" />
                    </div>

                    <div className="relative">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Account Status</label>
                        <select name="status" value={userData.status || 'inactive'} onChange={handleChange} className="w-full p-2 border rounded">
                            <option value="active">active</option>
                            <option value="pending_approval">pending_approval</option>
                            <option value="pending_edit_approval">pending_edit_approval</option>
                            <option value="rejected">rejected</option>
                            <option value="inactive">inactive</option>
                        </select>
                    </div>

                    {adminUser?.role === 'superadmin' && (
                        <>
                            <select name="role" value={userData.role || 'user'} onChange={handleChange} className="p-2 border rounded">
                                <option value="user">user</option>
                                <option value="admin">admin</option>
                                <option value="superadmin">superadmin</option>
                            </select>
                            {userData.role === 'admin' && (
                                <input name="assignedState" value={userData.assignedState || ''} onChange={handleChange} placeholder="Assigned State for Admin" className="p-2 border rounded" />
                            )}
                        </>
                    )}
                </div>

                <h3 className="text-xl font-bold text-gray-700 mt-6 border-b pb-2">Subscription & Payment Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Subscription Status</label>
                        <select name="subscriptionStatus" value={userData.subscriptionStatus || 'inactive'} onChange={handleChange} className="w-full p-2 border rounded mt-1 bg-white">
                            <option value="active">active</option>
                            <option value="inactive">inactive</option>
                            <option value="expired">expired</option>
                            <option value="cancelled">cancelled</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Expires At</label>
                        <input type="datetime-local" name="subscriptionExpiresAt" value={userData.subscriptionExpiresAt ? userData.subscriptionExpiresAt.slice(0, 16) : ''} onChange={handleChange} className="w-full p-2 border rounded mt-1" />
                    </div>
                    {userData.razorpayOrderId && (
                        <div className="md:col-span-2 text-sm text-gray-500">
                            <strong>Razorpay Order ID:</strong> {userData.razorpayOrderId}
                        </div>
                    )}
                </div>

                <h3 className="text-xl font-bold text-gray-700 mt-6 border-b pb-2">Medical Profile</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-red-50 p-4 border border-red-100 rounded-lg">
                    <input name="bloodGroup" value={userData.bloodGroup || ''} onChange={handleChange} placeholder="Blood Group (e.g., O+, A-)" className="p-2 border rounded" />
                    <label className="flex items-center space-x-2 p-2 border rounded bg-white">
                        <input type="checkbox" name="isOrganDonor" checked={!!userData.isOrganDonor} onChange={handleChange} className="w-4 h-4 text-brand-primary" />
                        <span className="text-gray-700 font-medium">Registered Organ Donor</span>
                    </label>
                    <input name="allergies" value={userData.allergies || ''} onChange={handleChange} placeholder="Known Allergies" className="p-2 border rounded md:col-span-2" />
                    <input name="medicalConditions" value={userData.medicalConditions || ''} onChange={handleChange} placeholder="Chronic Medical Conditions" className="p-2 border rounded md:col-span-2" />
                    <textarea name="currentMedications" value={userData.currentMedications || ''} onChange={handleChange} placeholder="Current Medications & Dosages" className="p-2 border rounded md:col-span-2" rows="2"></textarea>
                    <textarea name="vaccinationRecords" value={userData.vaccinationRecords || ''} onChange={handleChange} placeholder="Vaccination Records (e.g., COVID-19, Tetanus)" className="p-2 border rounded md:col-span-2" rows="2"></textarea>
                    <input name="primaryCarePhysicianName" value={userData.primaryCarePhysicianName || ''} onChange={handleChange} placeholder="Primary Care Physician Name" className="p-2 border rounded" />
                    <input name="primaryCarePhysicianPhone" value={userData.primaryCarePhysicianPhone || ''} onChange={handleChange} placeholder="Physician Contact Number" className="p-2 border rounded" />
                </div>

                <h3 className="text-xl font-bold text-gray-700 mt-6 border-b pb-2">Emergency Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-orange-50 p-4 border border-orange-100 rounded-lg">
                    <input name="emergencyContactName" value={userData.emergencyContactName || ''} onChange={handleChange} placeholder="Contact Name" className="p-2 border rounded" />
                    <input name="emergencyContactRelation" value={userData.emergencyContactRelation || ''} onChange={handleChange} placeholder="Relation (e.g., Spouse, Parent)" className="p-2 border rounded" />
                    <input name="emergencyContactPhone" value={userData.emergencyContactPhone || ''} onChange={handleChange} placeholder="Contact Phone" className="p-2 border rounded" />
                </div>

                {adminUser?.role === 'superadmin' && (
                    <div className="border-t pt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Set New Password</label>
                        <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Leave blank to keep current password" className="w-full p-2 border rounded" />
                    </div>
                )}

                {adminUser?.role === 'superadmin' && (
                    <div className="border-t pt-4 flex items-center justify-between">
                        <label htmlFor="isHighlighted" className="text-sm font-medium text-gray-700">Highlight this member on the main page</label>
                        <label htmlFor="isHighlighted" className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                id="isHighlighted"
                                name="isHighlighted"
                                className="sr-only peer"
                                checked={userData.isHighlighted}
                                onChange={handleChange}
                            />
                            <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-focus:ring-4 peer-focus:ring-red-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
                        </label>
                    </div>
                )}

                <textarea name="bio" value={userData.bio || ''} onChange={handleChange} placeholder="Bio" className="w-full p-2 border rounded" rows="4"></textarea>
                <div className="flex justify-end gap-4">
                    <button type="button" onClick={() => navigate('/portal-management-7a8b9c/manage-members')} className="bg-gray-500 text-white font-bold py-2 px-4 rounded">Cancel</button>
                    <button type="submit" className="bg-brand-primary text-white font-bold py-2 px-4 rounded">Save All Changes</button>
                </div>
                {message && <p className="text-center mt-4">{message}</p>}
            </form>
        </div>
    );
}

export default AdminEditUserForm;